export const VIEW_W = 960;
export const VIEW_H = 540;
export const TILE_SIZE = 32;

export const HOME_TILE_X = 0;
export const HOME_TILE_Y = 0;
export const PLAYER_START_X = TILE_SIZE * 0.5;
export const PLAYER_START_Y = TILE_SIZE * 1.5;
export const PLAYER_BASE_SPEED = 118;
export const PLAYER_BASE_HARVEST_POWER = 1;
export const PLAYER_START_INVENTORY_SLOTS = 10;
export const BASE_GATHER_SECONDS = 1;
export const GATHERER_BASE_SECONDS = 2.8;
export const CARRIER_DRONE_BASE_SPEED = 72;
export const CARRIER_DRONE_UPGRADED_SPEED = 116;
export const DRILL_STEP_SECONDS = 0.9;
export const GENERATED_TILE_VIEW_RADIUS_X = 18;
export const GENERATED_TILE_VIEW_RADIUS_Y = 12;
export const PLAYER_VISION_RADIUS = 7;
export const FEATURE_PICKUP_STREAK = true;
export const RESOURCE_STACK_LIMIT = 20;
export const TRASH_STACK_LIMIT = 10;
export const COMBO_WINDOW_SECONDS = 4;
export const COMBO_GATHER_SPEED_PER_TIER = 0.1;
export const COMBO_MOVE_SPEED_PER_TIER = 0.08;
export const GOO_SPREAD_INTERVAL_SECONDS = 5;
export const GOO_MOVEMENT_MULTIPLIER = 0.42;

export function streakThreshold(tier: number): number {
  return 4 + tier * tier * 2;
}

export const RESOURCE_IDS = ['scrap', 'plastic', 'glass', 'biomass'] as const;
export type ResourceId = (typeof RESOURCE_IDS)[number];

export type ResourceBag = Record<ResourceId, number>;
export type ToolRecipeId = 'grenade' | 'flamethrower';

export const RESOURCE_LABELS: Record<ResourceId, string> = {
  scrap: 'Scrap',
  plastic: 'Plastic',
  glass: 'Glass',
  biomass: 'Biomass',
};

export const SPRITE_IDS = [
  'player_reclaimer',
  'home_recycler',
  'storage_depot',
  'gatherer',
  'carrier_drone',
  'drill',
  'grenade',
  'flamethrower',
  'garbage_pile_1',
  'garbage_pile_2',
  'garbage_pile_3',
  'garbage_pile_4',
  'garbage_pile_5',
  'garbage_pile_6',
] as const;

export type SpriteId = (typeof SPRITE_IDS)[number];

export type ZoneId = 'intro' | 'north' | 'east' | 'south' | 'west';

export interface ZoneDefinition {
  id: ZoneId;
  name: string;
  minX: number;
  minY: number;
  width: number;
  height: number;
  color: number;
}

export const ZONES: readonly ZoneDefinition[] = [
  { id: 'intro', name: 'Starter Yard', minX: -3, minY: -2, width: 7, height: 6, color: 0x4a624a },
  { id: 'north', name: 'North Ruins', minX: -6, minY: -11, width: 12, height: 9, color: 0x405b48 },
  { id: 'east', name: 'Spill Basin', minX: 4, minY: -2, width: 10, height: 10, color: 0x4d6045 },
  { id: 'south', name: 'South Yard', minX: -6, minY: 8, width: 12, height: 9, color: 0x3f5660 },
  { id: 'west', name: 'West Yard', minX: -14, minY: -2, width: 11, height: 10, color: 0x5b5140 },
];

export const GOO_ZONE_ID: ZoneId = 'east';
export const GOO_SOURCE_TILES: readonly string[] = ['6,0', '10,2', '7,5'];

export const PERMANENT_WALL_TILES: readonly string[] = [
  '-5,-10',
  '-4,-10',
  '-3,-10',
  '1,-10',
  '2,-10',
  '3,-10',
  '4,-10',
  '-5,-9',
  '-1,-9',
  '4,-9',
  '-5,-8',
  '-1,-8',
  '1,-8',
  '2,-8',
  '4,-8',
  '-5,-7',
  '-4,-7',
  '-1,-7',
  '2,-7',
  '4,-7',
  '-2,-6',
  '-1,-6',
  '2,-6',
  '4,-6',
  '-5,-5',
  '-4,-5',
  '-3,-5',
  '2,-5',
  '3,-5',
  '4,-5',
  '-5,-4',
  '0,-4',
  '4,-4',
  '-5,-3',
  '-4,-3',
  '0,-3',
  '1,-3',
  '4,-3',
];

export type TechCategory = 'combo' | 'automation' | 'optimization';
export type TechId =
  | 'gather_speed'
  | 'movement_speed'
  | 'trash_traversal'
  | 'gatherer'
  | 'carrier_drone'
  | 'grenade'
  | 'recycler_efficiency'
  | 'flamethrower'
  | 'drill';

export interface TechDefinition {
  id: TechId;
  category: TechCategory;
  name: string;
  description: string;
  maxLevel: number;
  cost: number;
}

export const TECH_DEFINITIONS: readonly TechDefinition[] = [
  { id: 'gather_speed', category: 'combo', name: 'Flow Gathering', description: 'Each combo tier speeds gathering by 10% per level.', maxLevel: 2, cost: 1 },
  { id: 'movement_speed', category: 'combo', name: 'Flow Movement', description: 'Each combo tier speeds movement by 8% per level.', maxLevel: 2, cost: 1 },
  { id: 'trash_traversal', category: 'optimization', name: 'Trash Traversal', description: 'Dormant while all trash acts as a barrier.', maxLevel: 1, cost: 1 },
  { id: 'gatherer', category: 'automation', name: 'Gatherer', description: 'Unlock a building that gathers nearby trash. Second level increases range.', maxLevel: 2, cost: 1 },
  { id: 'carrier_drone', category: 'automation', name: 'Carrier Drone', description: 'Unlock a drone that carries trash from player to recycler. Second level speeds it up.', maxLevel: 2, cost: 1 },
  { id: 'grenade', category: 'optimization', name: 'Grenade', description: 'Craft one-use grenades that clear trash without resources.', maxLevel: 1, cost: 1 },
  { id: 'recycler_efficiency', category: 'optimization', name: 'Recycler Efficiency', description: 'Halve the chance that recycling yields nothing.', maxLevel: 1, cost: 1 },
  { id: 'flamethrower', category: 'optimization', name: 'Flamethrower', description: 'Craft a biomass-powered item that burns cone-shaped trash patches.', maxLevel: 1, cost: 1 },
  { id: 'drill', category: 'optimization', name: 'Drill', description: 'Unlock a biomass-fueled drill that carves a straight line through trash.', maxLevel: 1, cost: 1 },
];

export const ACTIVE_TECH_DEFINITIONS: readonly TechDefinition[] = TECH_DEFINITIONS.filter(
  (tech) => tech.category === 'combo' || tech.category === 'automation',
);

export const TECH_IDS = TECH_DEFINITIONS.map((tech) => tech.id);

export function createEmptyTechLevels(): Record<TechId, number> {
  return {
    gather_speed: 0,
    movement_speed: 0,
    trash_traversal: 0,
    gatherer: 0,
    carrier_drone: 0,
    grenade: 0,
    recycler_efficiency: 0,
    flamethrower: 0,
    drill: 0,
  };
}

export function techById(id: TechId): TechDefinition {
  return TECH_DEFINITIONS.find((tech) => tech.id === id) ?? TECH_DEFINITIONS[0];
}

export interface ToolRecipeDefinition {
  id: ToolRecipeId;
  name: string;
  description: string;
  resultItemId: ToolRecipeId;
  requiredTech: TechId;
  cost: ResourceBag;
  unique: boolean;
}

export const TOOL_RECIPE_DEFINITIONS: readonly ToolRecipeDefinition[] = [
  {
    id: 'grenade',
    name: 'Grenade',
    description: 'One-use thrown tool that chips and clears trash without yielding resources.',
    resultItemId: 'grenade',
    requiredTech: 'grenade',
    cost: { scrap: 2, plastic: 1, glass: 0, biomass: 0 },
    unique: false,
  },
  {
    id: 'flamethrower',
    name: 'Flamethrower',
    description: 'Reusable tool that burns trash in a cone and drains biomass while held.',
    resultItemId: 'flamethrower',
    requiredTech: 'flamethrower',
    cost: { scrap: 8, plastic: 4, glass: 2, biomass: 0 },
    unique: true,
  },
];

export function toolRecipeById(id: ToolRecipeId): ToolRecipeDefinition {
  return TOOL_RECIPE_DEFINITIONS.find((recipe) => recipe.id === id) ?? TOOL_RECIPE_DEFINITIONS[0];
}

export function createEmptyResources(): ResourceBag {
  return {
    scrap: 0,
    plastic: 0,
    glass: 0,
    biomass: 0,
  };
}

export function canAfford(resources: ResourceBag, cost: ResourceBag): boolean {
  return RESOURCE_IDS.every((id) => resources[id] >= cost[id]);
}

export function spendResources(resources: ResourceBag, cost: ResourceBag): void {
  for (const id of RESOURCE_IDS) resources[id] -= cost[id];
}
